import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";

interface MovieCardProps {
  movie: Movie;
  showSynopsis?: boolean;
}

export default function MovieCard({ movie, showSynopsis = false }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const fav = isFavorite(movie.id);
  const typeLabel = movie.type === "movie" ? "电影" : "剧集";

  return (
    <div className="group relative">
      <Link to={`/detail/${movie.id}`} className="block">
        <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
          <img
            src={movie.poster}
            alt={`《${movie.title}》海报`}
            title={`${movie.title} 高清影视`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/10 to-transparent opacity-80 group-hover:opacity-95 transition-opacity"></div>

          <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-500 text-primary-950 text-xs font-heading font-bold">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-star-fill text-[11px]"></i>
            </span>
            {movie.rating.toFixed(1)}
          </span>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              toggleFavorite(movie.id);
            }}
            aria-label={fav ? "取消收藏" : "收藏"}
            className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full backdrop-blur transition-colors cursor-pointer ${
              fav ? "bg-accent-500 text-primary-950" : "bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950"
            }`}
          >
            <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base`}></i>
          </button>

          {movie.type === "tv" && movie.episodeCount && (
            <span className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 text-[11px] font-label text-background-100">
              <span className="px-2 py-0.5 rounded bg-primary-950/70">
                更新至 {movie.currentEpisode}/{movie.episodeCount} 集
              </span>
            </span>
          )}

          <div className="absolute bottom-2 left-2 right-2 hidden group-hover:flex items-center gap-2">
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-label whitespace-nowrap">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-play-fill text-[11px]"></i>
              </span>
              查看详情
            </span>
          </div>
        </div>

        <div className="mt-2.5">
          <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
            {movie.title}
          </h3>
          <p className="mt-1 text-xs font-body text-foreground-500 line-clamp-1">
            {typeLabel} · {movie.region} · {movie.year}
          </p>
          {showSynopsis && (
            <p className="mt-1.5 text-xs font-body text-foreground-500 line-clamp-2 leading-relaxed">
              {movie.synopsis}
            </p>
          )}
        </div>
      </Link>
    </div>
  );
}